<script lang="ts">
	import type { ConfiguratorState } from '$lib/state/configurator.svelte';
	import type { Order } from '$models/Order/Order';
	import type {
		OrderConfigurationOrder,
		OrderConfigurator,
		OrderConfiguratorOptions
	} from '$models/OrderConfigurator/OrderConfigurator';
	import { page } from '$app/state';
	import { getSiteContext } from '$lib/context/site';
	import { inclusiveShopPresentation, setShopPresentation } from '$lib/shop/presentation.svelte';
	import {
		applyConfiguratorCoupon,
		resolveConfiguratorCoupon
	} from '$lib/state/configurator-coupons';
	import {
		createConfiguratorState,
		getShopPricing,
		setConfiguratorState
	} from '$lib/state/configurator.svelte';
	import { onMount, untrack } from 'svelte';
	import OrderConfiguratorCompact from './OrderConfiguratorCompact.svelte';
	import OrderConfiguratorConvertor from './OrderConfiguratorConvertor.svelte';
	import OrderConfiguratorDefault from './OrderConfiguratorDefault.svelte';
	import OrderConfiguratorDualStep from './OrderConfiguratorDualStep.svelte';
	import OrderConfiguratorShop from './OrderConfiguratorShop.svelte';

	interface Props {
		configurator: OrderConfigurator;
		onSubmit: (order: OrderConfigurationOrder, state: ConfiguratorState) => void;
		defaultOrder?: {
			frequency: number;
			productCategory?: any;
			products: { id: string; quantity: number }[];
			nonSubscribableProducts: any[]; // @TODO Implement and fix type
			couponCode: string | null;
		};
		recurringCouponCode?: string | null;
		onetimeCouponCode?: string | null;
		options?: OrderConfiguratorOptions;
		state?: ConfiguratorState;
	}

	// @TODO Some external onSubmit functions still expect a complete Order as argument. This does not break anything, but it needs to be revised
	let {
		configurator,
		onSubmit,
		defaultOrder,
		recurringCouponCode,
		onetimeCouponCode,
		options,
		state = $bindable()
	}: Props = $props();

	// If a defaultOrder is supplied, override the configuration defaults
	// @TODO First designed to be backwards compatible, implement in a better way
	if (defaultOrder) {
		configurator.configuration.categories = configurator.configuration.categories.map(
			(categoryItem) => {
				// Check if the products of this category includes a product from the defaultOrder
				const includesProduct = categoryItem.products.find(
					({ product }) => product.id === defaultOrder.products[0].id
				);

				// If no default category is set, set it if a product is found
				if (!defaultOrder.productCategory && includesProduct) {
					defaultOrder.productCategory = categoryItem.productCategory;
				}

				// Map all the products and set the quantities according to defaultOrder
				if (includesProduct) {
					categoryItem.products = categoryItem.products.map((productItem) => {
						const defaultProduct = defaultOrder.products.find(
							(defaultProduct) => defaultProduct.id === productItem.product.id
						);
						return {
							...productItem,
							quantity: defaultProduct ? defaultProduct.quantity : 0
						};
						return productItem;
					});
				}

				return categoryItem;
			}
		);

		if (defaultOrder.productCategory) {
			configurator.configuration.defaultCategory = defaultOrder.productCategory;
		}

		if (defaultOrder.frequency) {
			configurator.configuration.defaultFrequency = defaultOrder.frequency;
		}

		// If a coupon is passed on the default order, use it instead.
		// This should precede coupons because they usually come from cart.
		// Since we have no way of knowing which type it is, set both to this value.
		if (defaultOrder && defaultOrder.couponCode && defaultOrder.couponCode.length) {
			recurringCouponCode = defaultOrder.couponCode;
			onetimeCouponCode = defaultOrder.couponCode;
		}

		// @TODO also populate nonSubscribableProducts
	}

	onMount(() => {
		const resolvedCoupon = resolveConfiguratorCoupon();

		// URL/temp coupons intentionally override configured/default coupons, matching the
		// previous component-local behavior.
		if (resolvedCoupon) {
			applyConfiguratorCoupon(configuratorState, resolvedCoupon);
			return;
		}

		// If there is no URL/temp coupon, apply the configured recurring/onetime codes.
		configuratorState.setCouponCodes({
			recurringCode: recurringCouponCode || '',
			onetimeCode: onetimeCouponCode || ''
		});
	});

	const configuratorState = createConfiguratorState(configurator.configuration, {
		pricingSource: getShopPricing() && untrack(() => !defaultOrder) ? 'total-price' : 'order-price',
		...options
	});
	setConfiguratorState(configuratorState);
	// Cart/account editors must retain inclusive presentation even beneath a Shop page.
	if (!configuratorState.usesTotalPrice) setShopPresentation(inclusiveShopPresentation);
	const site = getSiteContext();

	// Update the bindable state
	state = configuratorState;

	function onSubmitInternal(order: OrderConfigurationOrder) {
		if (!configuratorState.canSubmitPricing()) return;
		onSubmit(order, configuratorState);
	}
</script>

{#if configurator.type === 'default'}
	<OrderConfiguratorDefault {configurator} onSubmit={onSubmitInternal} />
{:else if configurator.type === 'compact'}
	<OrderConfiguratorCompact {configurator} onSubmit={onSubmitInternal} />
{:else if configurator.type === 'dual_step'}
	<OrderConfiguratorDualStep {configurator} onSubmit={onSubmitInternal} />
{:else if configurator.type === 'shop'}
	<OrderConfiguratorShop {configurator} onSubmit={onSubmitInternal} />
{:else if configurator.type === 'convertor'}
	<OrderConfiguratorConvertor {configurator} onSubmit={onSubmitInternal} />
{/if}

<style lang="postcss" global>
	.step-section {
		& {
			max-width: 1000px;
			margin: 0 auto;
			scroll-margin-top: 160px;
		}

		&--small {
			max-width: var(--container-extra-small);
		}

		&--wide {
			max-width: 100%;
		}

		&:not(.hidden) {
			margin-bottom: var(--spacing-18);
		}

		&:last-child {
			margin-bottom: 0;
		}

		&-header {
			text-align: center;

			&:not(:empty) {
				margin-bottom: var(--spacing-8);
			}
		}

		&-title {
			font-family: var(--configurator-section-title-font-family);
			font-weight: var(--configurator-section-title-font-weight);
			font-size: var(--configurator-section-title-font-size);
			text-transform: var(--configurator-section-title-text-transform);
			line-height: var(--font-lineheight-1);
			text-transform: uppercase;
			color: var(--configurator-section-title-color);
			letter-spacing: 0.5px;
			margin-bottom: var(--spacing-1);
		}

		&-subtitle {
			font-family: var(--configurator-section-subtitle-font-family);
			font-weight: var(--configurator-section-subtitle-font-weight);
			font-size: var(--configurator-section-subtitle-font-size);
			text-transform: var(--configurator-section-subtitle-text-transform);
		}

		&-box {
			position: relative;
			text-align: center;
			padding: var(--spacing-8);
			color: var(--base-color);
			background-color: var(--color-white);
			border-radius: var(--box-border-radius);

			&-inner {
				position: relative;
				z-index: 2;
			}
		}

		&-text {
			margin-bottom: var(--spacing-4);
		}

		&-choices {
			display: flex;
			flex-wrap: wrap;
			justify-content: center;
			margin: 0 auto;
			max-width: 540px;

			@media (--sm) {
				flex-direction: row;
			}
		}

		&-choice {
			&-image {
				width: 100%;
				opacity: 0.5;
				transition: opacity var(--transition);
				margin: 0 12px;

				@media (--sm) {
					width: calc(50% - 24px);
				}

				&:not(:first-child) {
					margin-top: var(--spacing-8);

					@media (--sm) {
						margin-top: 0;
					}
				}

				.no-selection &,
				&.is-active {
					opacity: 1;
				}

				&:hover {
					opacity: 1;
				}

				&-holder {
					border-radius: 8px;
					overflow: hidden;
					transition: border-color var(--transition);
					margin-bottom: var(--spacing-5);
				}

				&-src {
					width: 100%;
					height: auto;
					aspect-ratio: 9 / 6;
					object-fit: cover;
				}

				&-button {
					width: 100%;
				}
			}

			&-button {
				margin: 0 4px;
				flex: 1;
				padding-left: var(--spacing-3);
				padding-right: var(--spacing-3);

				@media (--md) {
					flex: 0 1 auto;
					margin: 0 5px;
					padding-left: var(--button-spacing-y);
					padding-right: var(--button-spacing-y);
				}

				&.is-active {
					color: var(--color-white);
					background-color: var(--button-background-hover);
				}
			}
		}
	}

	.step {
		& {
			color: var(--base-color);
			background-color: white;
			border-radius: var(--box-border-radius);
			overflow: hidden;
		}

		&-inner {
			@media (--md) {
				display: flex;
				align-items: stretch;
			}
		}

		&-main {
			padding: var(--spacing-6);
			flex: 1;

			@media (--md) {
				padding: var(--spacing-8);
			}
		}

		&-header {
			margin-bottom: var(--spacing-6);
		}

		&-title {
			font-size: var(--configurator-box-title-font-size);
			font-family: var(--configurator-box-title-font-family);
			font-weight: var(--configurator-box-title-font-weight);
			text-transform: var(--configurator-box-title-text-transform);
			line-height: var(--font-lineheight-0);
			margin-bottom: var(--spacing-2);
		}

		&-subtitle {
			font-size: var(--font-size-0);
			line-height: var(--font-lineheight-0);
			color: var(--color-grey);
		}

		&-products {
			margin-bottom: var(--spacing-6);
		}

		&-product {
			display: flex;
			align-items: center;

			&:not(:last-child) {
				margin-bottom: var(--spacing-3);
			}

			&-title {
				flex: 1;
				font-size: var(--configurator-product-list-title-font-size);
				font-family: var(--configurator-product-list-title-font-family);
				font-weight: var(--configurator-product-list-title-font-weight);
				text-transform: var(--configurator-product-list-title-text-transform);
				line-height: var(--font-lineheight-0);
				color: var(--configurator-product-list-title-color);
				padding-right: var(--spacing-4);
			}

			&-input {
				display: flex;
				align-items: stretch;
				border: 1px solid var(--border-color);
				max-width: 108px;
				border-radius: 18px;

				input {
					padding: 8px;
					font-size: 14px;
					border: 0;
					text-align: center;
					flex: 1;
					min-height: 36px;
				}

				button {
					display: block;
					position: relative;
					padding: 4px 14px;
					color: var(--color-espresso);
					min-height: 36px;

					&:before,
					&:after {
						content: '';
						position: absolute;
						top: 17px;
						left: 8px;
						width: 10px;
						height: 2px;
						border-radius: 1px;
						background: var(--color-espresso);
					}

					&:after {
						transform: rotate(90deg);
					}

					&:first-child {
						&:before,
						&:after {
							left: 14px;
						}
						&:after {
							display: none;
						}
					}
				}
			}
		}

		&-totals {
			display: flex;
			flex-direction: column;
			align-items: flex-end;
			margin-bottom: var(--spacing-2);

			&:before {
				content: '';
				display: block;
				width: 60px;
				height: 1.5px;
				border-radius: 0.75px;
				background-color: var(--color-brown);
				margin-bottom: var(--spacing-2);
			}

			&-inner {
				position: relative;
				display: flex;
				align-items: center;
				min-height: 54px;
			}

			&-count {
				white-space: nowrap;
				font-size: var(--font-size-2);
				line-height: var(--font-lineheight-0);
			}

			&-error {
				position: absolute;
				top: 50%;
				right: calc(100% + 12px);
				transform: translateY(-50%);
				font-size: 13px;
				text-align: right;
				line-height: var(--font-lineheight-1);
				padding: 10px 12px 10px 35px;
				color: white;
				white-space: nowrap;
				background-color: var(--color-red);
				background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNCI+PHBhdGggZmlsbD0iI0ZGRiIgZmlsbC1ydWxlPSJldmVub2RkIiBkPSJtOCAwIDggMTRIMEw4IDBaTTcgNC45NzZjMC0uNTU3LjQ0NC0xLjAxIDEtMS4wMS41NTIgMCAxIC40NDggMSAxLjAxdjMuOTMxYzAgLjU1OC0uNDQ0IDEuMDEtMSAxLjAxLS41NTIgMC0xLS40NDgtMS0xLjAxVjQuOTc2Wk03IDExLjlhLjk5My45OTMgMCAwIDEgMS0uOTkyYy41NTIgMCAxIC40NCAxIC45OTJhLjk5My45OTMgMCAwIDEtMSAuOTkyYy0uNTUyIDAtMS0uNDQtMS0uOTkyWiIvPjwvc3ZnPg==');
				background-size: 16px 14px;
				background-position: 11px calc(50% - 1px);
				background-repeat: no-repeat;
				border-radius: var(--radius-2);

				&:after {
					content: '';
					position: absolute;
					top: calc(50% - 8px);
					right: -0.375rem;
					width: 0;
					height: 0;
					border-top: 8px solid transparent;
					border-bottom: 8px solid transparent;
					border-left: 8px solid var(--color-red);
				}
			}
			&-success {
				position: absolute;
				top: 50%;
				right: calc(100% + 12px);
				transform: translateY(-50%);
				font-size: 13px;
				text-align: right;
				line-height: var(--font-lineheight-1);
				padding: 10px 12px;
				color: var(--color-brown);
				white-space: nowrap;
				background-color: var(--color-green);
				border-radius: var(--radius-2);

				&:after {
					content: '';
					position: absolute;
					top: calc(50% - 8px);
					right: -0.375rem;
					width: 0;
					height: 0;
					border-top: 8px solid transparent;
					border-bottom: 8px solid transparent;
					border-left: 8px solid var(--color-green);
				}
			}
		}

		&-footer {
			display: flex;
			align-items: center;
		}

		&-discount {
			&-price {
				display: flex;
				flex-wrap: wrap;
				line-height: var(--font-lineheight-2);

				&-value {
					width: 100%;
					font-size: var(--font-size-3);
				}
				&-per-unit {
					display: block;
					width: 100%;
					font-size: var(--font-size-0);
					color: var(--color-grey);
					margin-top: var(--spacing-1);
				}
			}
		}

		&-price {
			line-height: var(--font-lineheight-0);
			&-label {
				color: var(--color-grey);
				font-size: var(--font-size-0);
				line-height: var(--font-lineheight-0);
				margin-bottom: 4px;
			}
			&-count {
				font-size: var(--font-size-4);
				line-height: var(--font-lineheight-0);
			}
		}

		&-button {
			margin-left: auto;

			button {
				&.disabled {
					opacity: 0.4;
					cursor: not-allowed;
				}
			}
		}

		&-image {
			display: none;

			@media (--md) {
				display: block;
				width: 45%;
				position: relative;
			}

			img {
				display: block;
				position: absolute;
				top: 0;
				left: 0;
				right: 0;
				bottom: 0;
				width: 100%;
				height: 100%;
				object-fit: cover;
			}
		}
	}

	.step-extras {
		color: var(--color-brown);
		padding: var(--spacing-5);
		background-color: var(--color-white);

		@media (--xs) {
			padding: var(--spacing-8);
		}

		&-slider {
			.slider {
				--slide-size: 90%;

				@media (--sm) {
					--slide-size: calc(100% / 3);
				}

				@media (--lg) {
					--slide-size: calc(100% / 4);
				}
			}

			&--count-1,
			&--count-2,
			&--count-3 {
				.slider {
					&-container {
						@media (--sm) {
							justify-content: center;
						}
					}
				}
			}
		}

		&-footer {
			display: flex;
			flex-direction: column;
			align-items: center;

			@media (--xs) {
				align-items: flex-end;
			}
		}

		&-subtotal {
			margin-top: var(--spacing-6);
			margin-bottom: var(--spacing-4);
			font-size: var(--font-size-3);

			@media (--sm) {
				font-size: var(--font-size-4);
			}
		}

		&-submit {
			display: flex;
			justify-content: center;
			width: 100%;

			@media (--xs) {
				width: auto;
			}

			.button {
				width: 100%;

				@media (--xs) {
					width: auto;
				}
			}
		}
	}

	.step-extra {
		& {
			text-align: center;
		}
		&-image {
			margin-bottom: var(--spacing-5);

			img {
				display: block;
				width: 100%;
				height: auto;
				aspect-ratio: 1/1;
				object-fit: cover;
			}
		}

		&-name {
			display: block;
			font-size: var(--font-size-0);
			line-height: var(--font-lineheight-0);
			margin-bottom: var(--spacing-1);

			@media (--md) {
				font-size: var(--font-size-2);
				line-height: var(--base-lineheight);
			}
		}

		&-price {
			display: block;
			margin-bottom: var(--spacing-3);
		}

		&-quantity {
			display: flex;
			align-items: stretch;
			margin: 0 auto;
			max-width: 136px;

			input {
				flex: 1;
				padding: 8px;
				font-size: 14px;
				border-left: 0;
				border-right: 0;
				text-align: center;
			}

			button {
				display: flex;
				align-items: center;
				justify-content: center;
				width: 40px;
				height: 40px;
				color: white;
				background-color: var(--color-primary);
			}
		}
	}

	/*
	 * Migrated from old configurator.
	 * @TODO: Check and remove unused styles.
	 */
	.frequency-step {
		&-inner {
			@media (--md) {
				display: flex;
				justify-content: space-between;
			}
		}

		&-box {
			display: flex;
			flex-direction: column;
			color: var(--base-color);
			background-color: white;
			border: var(--configurator-box-border);
			border-radius: var(--box-border-radius);
			padding: var(--spacing-6);

			@media (--lg) {
				padding: var(--spacing-8);
			}

			&:first-child {
				@media (--md) {
					width: 58%;
				}
			}

			&:last-child {
				@media (--md) {
					width: 38%;
				}
			}

			&--centered {
				@media (--md) {
					width: 58% !important;
					margin: 0 auto;
				}
			}
		}

		&-spacer {
			display: flex;
			align-items: center;
			text-align: center;
			margin: var(--spacing-4) 0;

			@media (--md) {
				display: none;
			}

			&:after,
			&:before {
				content: '';
				flex: 1;
				display: block;
				height: 1px;
				background-color: var(--color-primary);

				.variant-dark & {
					background-color: var(--color-white);
				}
			}

			span {
				display: block;
				color: var(--color-primary);
				padding: 0 var(--spacing-2);

				.variant-dark & {
					color: var(--color-white);
				}
			}
		}

		&-title {
			font-size: var(--configurator-box-title-font-size);
			font-family: var(--configurator-box-title-font-family);
			font-weight: var(--configurator-box-title-font-weight);
			text-transform: var(--configurator-box-title-text-transform);
			line-height: var(--font-lineheight-0);
			margin-bottom: var(--spacing-4);
		}

		&-select {
			display: flex;
			align-items: center;
			margin-bottom: var(--spacing-6);

			&-label {
				font-size: var(--font-size-2);
				color: var(--color-brown);
				margin-right: var(--spacing-4);
			}

			&-input {
				flex: 1;
				max-width: 154px;
				border-radius: var(--configurator-input-radius);
			}
		}

		&-text {
			font-size: var(--configurator-frequency-font-size);
			line-height: var(--font-lineheight-2);
			color: var(--color-brown);
			margin-bottom: var(--spacing-6);

			:global(p) {
				&:not(:last-child) {
					margin-bottom: var(--spacing-5);
				}
			}

			:global(li) {
				position: relative;
				padding-left: 24px;

				&:before {
					content: '';
					position: absolute;
					top: 3px;
					left: 0;
					display: flex;
					width: 16px;
					height: 16px;
					margin-right: var(--spacing-2);
					border-radius: 50%;
					border: var(--configurator-frequency-usp-border);
					background-color: var(--configurator-frequency-usp-background);
					background-image: var(--configurator-frequency-usp-icon);
					background-size: var(--configurator-frequency-usp-icon-size);
					background-position: center center;
					background-repeat: no-repeat;
				}

				&:not(:last-child) {
					margin-bottom: var(--spacing-2);
				}
			}
		}

		&-price {
			flex: 1;
			display: flex;
			flex-wrap: wrap;
			line-height: var(--font-lineheight-0);
			margin-bottom: var(--spacing-4);

			@media (--lg) {
				margin-bottom: 0;
			}

			&-label {
				width: 100%;
				color: var(--color-promo);
				font-size: var(--font-size-0);
				margin-bottom: var(--spacing-1);
			}

			&-value {
				font-size: var(--font-size-4);
			}

			&-after,
			&-old {
				font-size: var(--font-size-0);
				color: var(--color-grey);
				align-self: flex-end;
				margin-left: var(--spacing-2);
				margin-bottom: 2px;
			}

			&-old {
				text-decoration: line-through;
			}

			&-per-unit {
				display: flex;
				align-items: center;
				width: 100%;
				font-size: var(--font-size-0);
				color: var(--color-grey);
				margin-top: var(--spacing-1);
			}
		}

		&-tooltip {
			display: block;
			position: relative;
			margin-left: 7px;

			&-trigger {
				display: flex;
				align-items: center;
				justify-content: center;
				width: 16px;
				height: 16px;
				font-family: 'Georgia', 'Times New Roman', serif;
				font-weight: bold;
				font-size: 11px;
				line-height: 1;
				color: var(--color-white);
				background-color: var(--color-primary);
				border-radius: 50%;
			}

			&-label {
				display: block;
				opacity: 0;
				visibility: hidden;
				position: absolute;
				bottom: calc(100% + 6px);
				left: 50%;
				transform: translateX(-50%);
				white-space: nowrap;
				padding: 6px 8px;
				font-size: 12px;
				line-height: 1;
				color: var(--color-brown);
				background-color: var(--color-secondary);
				transition: all var(--transition);

				&:after {
					content: '';
					display: block;
					position: absolute;
					bottom: -6px;
					left: 50%;
					transform: translateX(-50%);
					border-left: 6px solid transparent;
					border-right: 6px solid transparent;
					border-top: 6px solid var(--color-secondary);
				}
			}

			&:hover {
				cursor: help;
				.frequency-step-tooltip-label {
					opacity: 1;
					visibility: visible;
				}
			}
		}

		&-footer {
			margin-top: auto;
			width: 100%;

			@media (--lg) {
				display: flex;
				align-items: center;
			}
		}

		&-button {
			position: relative;
			display: flex;
			flex-direction: column;

			@media (--lg) {
				margin-left: auto;
			}

			.button {
				display: block;
				width: 100%;

				@media (--lg) {
					width: auto;
				}
			}
		}

		&-savings {
			display: flex;
			align-items: center;
			position: relative;
			padding: var(--spacing-1) var(--spacing-3);
			margin: var(--spacing-3) auto 0 auto;
			font-size: 11px;
			font-family: var(--font-secondary);
			font-weight: normal;
			text-transform: uppercase;
			color: var(--color-brown);
			text-align: center;
			white-space: nowrap;
			line-height: var(--font-lineheight-1);
			border-radius: var(--radius-2);
			background-color: color-mix(in srgb, var(--color-green) 25%, var(--color-white));

			@media (--md) {
				position: absolute;
				z-index: 2;
				left: 0;
				bottom: calc(100% + 12px);
				left: 75%;
				transform: translateX(-75%);
			}

			@media (--lg) {
				left: 50%;
				transform: translateX(-50%);
			}

			&:before {
				content: '';
				display: block;
				margin-right: 4px;
				width: 12px;
				height: 9px;
				background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iOSIgdmlld0JveD0iMCAwIDEyIDkiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMTAuODI3OCAwLjEwOTg2NkMxMC42ODMgLTAuMDM2NjIxOSAxMC40NDMyIC0wLjAzNjYyMTkgMTAuMjk3NCAwLjEwOTg2Nkw0LjcwNTcxIDUuNjc3MzdDNC41NTk4NyA1LjgyNTg1IDQuMzIxMTMgNS44MjU4NSA0LjE3NTMgNS42NzczN0wxLjcyNTA1IDMuMTgxMTNDMS42NTMxMyAzLjEwODM4IDEuNTU3MjQgMy4wNzE0OSAxLjQ2MjM1IDMuMDcxNDlDMS4zNjU0NSAzLjA3MDUgMS4yNjc1NyAzLjEwNjM5IDEuMTkzNjUgMy4xODExM0wwLjExMTg3NCA0LjE1NTcxQzAuMDM4OTU1NyA0LjIyOTQ2IDAgNC4zMjIxMSAwIDQuNDE4NzdDMCA0LjUxNTQzIDAuMDM4OTU1NyA0LjYxODA4IDAuMTExODc0IDQuNjkwODNMNC4xNzUzIDguODkwMTNDNC4zMjExMyA5LjAzNjYyIDQuNTU5ODcgOS4wMzY2MiA0LjcwNTcxIDguODkwMTNMMTEuODkwNiAxLjcxNjI1QzEyLjAzNjUgMS41Njk3NiAxMi4wMzY1IDEuMzI3NTkgMTEuODkwNiAxLjE4MTFMMTAuODI3OCAwLjEwOTg2NloiIGZpbGw9IiM1OTJlMmMiLz4KPC9zdmc+Cg==');
				background-size: 10px 8px;
				background-position: center left;
				background-repeat: no-repeat;
			}

			&:after {
				content: '';
				position: absolute;
				top: -12px;
				left: 50%;
				border: 6px solid rgba(0, 0, 0, 0);
				border-bottom-color: color-mix(in srgb, var(--color-green) 25%, var(--color-white));

				@media (--md) {
					top: auto;
					bottom: -12px;
					border-bottom-color: transparent;
					border-top-color: color-mix(in srgb, var(--color-green) 25%, var(--color-white));
				}
			}
		}
	}
</style>
